import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Row,
  Space,
  Card,
  Form,
  Col,
  Typography,
  Divider,
  Input,
  Checkbox,
  notification,
  List,
} from "antd";
import "./index.scss";
import debounce from "lodash.debounce";
import API_SERVICE from "shared/services/api-service";
import {
  CloseOutlined,
  DownOutlined,
  MinusOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "shared/redux/store";
import {
  setFilterData,
  setIsShowFilter,
  setLeadPageNo,
  setLoanType,
  setRestFilterData,
} from "shared/redux/lead-reducer";
import { getEnumList, getUserDetails } from "shared/services/Utility";

const { Title, Text } = Typography;

type HierarchyTreeFilterProps = {
  label: string;
  hierarchyList: any[];
  selectedKeys: any[];
  onSelectedKeysChange: (keys: any[]) => void;
  onSearch: (searchParam: string) => void;
  checkboxStyles: React.CSSProperties;
  /** Local filter only — no debounce. */
  immediateSearch?: boolean;
  emptyText?: string;
};

/** Search + expandable hierarchy checklist (connector / team). */
const HierarchyTreeFilter: React.FC<HierarchyTreeFilterProps> = ({
  label,
  hierarchyList,
  selectedKeys,
  onSelectedKeysChange,
  onSearch,
  checkboxStyles,
  immediateSearch = false,
  emptyText = "No matching team member",
}) => {
  const [expandedIds, setExpandedIds] = useState<any[]>([]);

  const getChildKeys = (listOfItem: any, childKeys: any[]) => {
    listOfItem?.forEach((item: any) => {
      childKeys.push(item.key);
      if (item?.children?.length > 0) {
        getChildKeys(item?.children, childKeys);
      }
    });
    return childKeys;
  };

  const onCheckboxUnTick = (item: any) => {
    let keys = [...selectedKeys];
    let childKeys = [item.key] as any[];
    if (item?.children?.length > 0) {
      childKeys = getChildKeys(item.children, childKeys);
    }
    childKeys.forEach((keySplice) => {
      const idx = keys.findIndex((key) => key == keySplice);
      if (idx > -1) {
        keys.splice(idx, 1);
      }
    });
    onSelectedKeysChange(keys);
  };

  const onCheckboxTick = (item: any) => {
    let keys = [...selectedKeys];
    let childKeys = [item.key] as any[];
    if (item?.children?.length > 0) {
      childKeys = getChildKeys(item.children, childKeys);
    }
    childKeys.forEach((keySplice) => {
      if (keys.findIndex((key) => key == keySplice) === -1) {
        keys.push(keySplice);
      }
    });
    onSelectedKeysChange(keys);
  };

  const onCheckboxClick = (item: any) => {
    if (selectedKeys.findIndex((key) => key == item.key) > -1) {
      onCheckboxUnTick(item);
    } else {
      onCheckboxTick(item);
    }
  };

  const onExpand = (item: any) => {
    const keys = [...expandedIds];
    const idx = keys.findIndex((key) => key == item.key);
    if (idx > -1) {
      keys.splice(idx, 1);
    } else {
      keys.push(item.key);
    }
    setExpandedIds(keys);
  };

  const renderList = (item: any, children: any) => {
    const isExpanded = expandedIds.find((key) => key == item.key);
    const isChecked = selectedKeys.find((key) => key == item.key);
    return (
      <div style={{ marginLeft: "20px" }} className="lead-filter-hierarchy__node">
        <div className="lead-filter-hierarchy__row">
          <Button
            style={{
              marginRight: "5px",
              height: "20px",
              width: "20px",
              padding: "0px",
              border: "none",
              flexShrink: 0,
            }}
            onClick={() => onExpand(item)}
          >
            {item.children && (isExpanded ? <MinusOutlined /> : <PlusOutlined />)}
          </Button>
          <Checkbox
            style={checkboxStyles}
            className="dynamic-pagination-checkbox"
            checked={!!isChecked}
            onClick={() => onCheckboxClick(item)}
          >
          <Text className="lead-filter-hierarchy__title" title={item?.title}>
            {item?.title}
          </Text>
          </Checkbox>
        </div>
        {item?.children?.length > 0 &&
          isExpanded &&
          children.map((data: any, idx: number) => (
            <React.Fragment key={data?.key ?? idx}>
              {renderList(data, data?.children)}
            </React.Fragment>
          ))}
      </div>
    );
  };

  return (
    <>
      <Form.Item label={label}>
        <Input
          size="large"
          className="input-search-box"
          placeholder="Search"
          allowClear
          prefix={<SearchOutlined />}
          onChange={
            immediateSearch
              ? (e) => onSearch(e?.target?.value ?? "")
              : debounce((e: React.ChangeEvent<HTMLInputElement>) => {
                  onSearch(e?.target?.value ?? "");
                }, 600)
          }
        />
      </Form.Item>
      <Form.Item>
        <Card style={{ maxHeight: "270px", overflow: "auto" }}>
          {hierarchyList.length === 0 ? (
            <div className="lead-filter-flat-list__empty">{emptyText}</div>
          ) : (
          <List
            style={{ width: "100%" }}
            grid={{ gutter: 24, column: 1, xs: 1, sm: 1, md: 1, lg: 1 }}
            itemLayout="horizontal"
            dataSource={hierarchyList}
            renderItem={(item: any) => (
              <List.Item style={{ padding: "0px", marginLeft:"-20px" }}>
                {renderList(item, item?.children)}
              </List.Item>
            )}
          />
          )}
        </Card>
      </Form.Item>
    </>
  );
};

type FlatCheckboxListFilterProps = {
  label: string;
  items: { key: string; title: string }[];
  selectedKeys: string[];
  onSelectedKeysChange: (keys: string[]) => void;
  onSearch: (searchParam: string) => void;
  checkboxStyles: React.CSSProperties;
  hasMore?: boolean;
  onLoadMore?: () => void;
  emptyText?: string;
};

/** Search + flat checklist (paginated connector list). */
const FlatCheckboxListFilter: React.FC<FlatCheckboxListFilterProps> = ({
  label,
  items,
  selectedKeys,
  onSelectedKeysChange,
  onSearch,
  checkboxStyles,
  hasMore,
  onLoadMore,
  emptyText = "No connectors found",
}) => {
  return (
    <>
      <Form.Item label={label}>
        <Input
          size="large"
          className="input-search-box"
          placeholder="Search"
          allowClear
          prefix={<SearchOutlined />}
          onChange={debounce((e: React.ChangeEvent<HTMLInputElement>) => {
            onSearch(e?.target?.value ?? "");
          }, 600)}
        />
      </Form.Item>
      <Form.Item>
        <Card style={{ maxHeight: "270px", overflow: "auto" }} className="lead-filter-flat-list">
          {items.length === 0 ? (
            <div className="lead-filter-flat-list__empty">{emptyText}</div>
          ) : (
            <Checkbox.Group
              value={selectedKeys}
              onChange={(vals) => onSelectedKeysChange(vals as string[])}
            >
              <div className="lead-filter-flat-list__list">
                {items.map((item) => (
                  <div key={item.key} className="lead-filter-flat-list__row">
                    <Checkbox
                      value={item.key}
                      className="dynamic-pagination-checkbox lead-filter-flat-list__checkbox"
                      style={checkboxStyles}
                    >
                      <span className="lead-filter-flat-list__label" title={item.title}>
                        {item.title}
                      </span>
                    </Checkbox>
                  </div>
                ))}
              </div>
            </Checkbox.Group>
          )}
          {hasMore && onLoadMore && (
            <Button type="link" block onClick={onLoadMore} className="lead-filter-flat-list__load-more">
              Load more
            </Button>
          )}
        </Card>
      </Form.Item>
    </>
  );
};

type LoanTypePickerProps = {
  value?: string[];
  onChange?: (v: string[]) => void;
  checkboxStyles: React.CSSProperties;
  loanTypeEnum: { key: string; value: string }[];
};

/** Loan type: “Select Product” row + checklist (checkbox left, label right). */
const LoanTypePicker: React.FC<LoanTypePickerProps> = ({
  value = [],
  onChange,
  checkboxStyles,
  loanTypeEnum,
}) => {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(true);
  const [search, setSearch] = useState("");

  const selected = Array.isArray(value) ? value : [];
  const triggerLabel =
    selected.length === 0 ? "Select Product" : selected.join(", ");

  const filteredLoanTypes = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return loanTypeEnum;
    }
    return loanTypeEnum.filter(
      (o) =>
        o.value.toLowerCase().includes(query) || o.key.toLowerCase().includes(query)
    );
  }, [loanTypeEnum, search]);

  const handleChange = (visibleChecked: string[]) => {
    const filteredValues = new Set(filteredLoanTypes.map((o) => o.value));
    const hiddenSelected = selected.filter((v) => !filteredValues.has(v));
    const merged = Array.from(new Set([...hiddenSelected, ...visibleChecked]));
    onChange?.(merged);
    dispatch(setLoanType(merged.length ? merged : undefined));
  };

  return (
    <div className="lead-filter-loan-type">
      <button
        type="button"
        className="lead-filter-loan-type__trigger"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span
          className={
            selected.length
              ? "lead-filter-loan-type__trigger-text"
              : "lead-filter-loan-type__trigger-placeholder"
          }
        >
          {triggerLabel}
        </span>
        <DownOutlined className="lead-filter-loan-type__chevron" rotate={open ? 180 : 0} />
      </button>
      {open && (
        <div className="lead-filter-loan-type__panel">
          <Input
            size="large"
            className="input-search-box lead-filter-loan-type__search"
            placeholder="Search"
            allowClear
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => setSearch(e?.target?.value ?? "")}
          />
          <Checkbox.Group
            value={selected}
            onChange={(vals) => handleChange(vals as string[])}
          >
            <div className="lead-filter-loan-type__list">
              {filteredLoanTypes.length === 0 ? (
                <div className="lead-filter-flat-list__empty">No matching loan types</div>
              ) : (
                filteredLoanTypes.map((o) => (
                  <div key={o.key} className="lead-filter-loan-type__row">
                    <Checkbox
                      value={o.value}
                      className="dynamic-pagination-checkbox lead-filter-loan-type__checkbox"
                      style={checkboxStyles}
                    >
                      <span className="lead-filter-loan-type__row-label" title={o.value}>
                        {o.value}
                      </span>
                    </Checkbox>
                  </div>
                ))
              )}
            </div>
          </Checkbox.Group>
        </div>
      )}
    </div>
  );
};

/** Employee tree: payload is root node { key, title, children }. */
const parseEmployeeHierarchyResponse = (payload: any) => {
  if (payload?.key) {
    return [payload];
  }
  const root = payload?.employeeHierarchyDetails ?? null;
  return root ? [root] : [];
};

const hierarchyNodeMatchesSearch = (node: any, query: string) => {
  const title = (node.title ?? "").toLowerCase();
  const firstName = (node.firstName ?? "").toLowerCase();
  const lastName = (node.lastName ?? "").toLowerCase();
  const designation = (node.designation ?? "").toLowerCase();
  return (
    title.includes(query) ||
    firstName.includes(query) ||
    lastName.includes(query) ||
    designation.includes(query)
  );
};

/** Filter team hierarchy in UI only (no API). */
const filterHierarchyTree = (nodes: any[], searchParam: string): any[] => {
  const query = searchParam.trim().toLowerCase();
  if (!query) {
    return nodes;
  }

  const filterNode = (node: any): any | null => {
    const filteredChildren = (node.children ?? [])
      .map(filterNode)
      .filter(Boolean) as any[];

    if (hierarchyNodeMatchesSearch(node, query)) {
      return { ...node, children: node.children ?? [] };
    }
    if (filteredChildren.length > 0) {
      return { ...node, children: filteredChildren };
    }
    return null;
  };

  return nodes.map(filterNode).filter(Boolean) as any[];
};

/** Connector list: paginated { content: [...] }. */
const parseConnectorListResponse = (payload: any) => {
  if (!payload) {
    return { list: [], last: true, pageNo: 1 };
  }
  if (Array.isArray(payload)) {
    return { list: payload, last: true, pageNo: 1 };
  }
  return {
    list: payload.content ?? [],
    last: payload.last ?? true,
    pageNo: payload.pageNo ?? 1,
  };
};

const normalizeIdList = (ids: any): string[] => {
  if (Array.isArray(ids)) {
    return ids.filter((id) => id != null && id !== "");
  }
  if (ids != null && ids !== "") {
    return [ids];
  }
  return [];
};

const getAppliedEmployeeIds = (filterData: any) =>
  normalizeIdList(filterData?.employeeIds ?? filterData?.employeeId);

const getAppliedConnectorIds = (filterData: any) =>
  normalizeIdList(filterData?.connectorIds ?? filterData?.connectorId);

const isFilterApplied = (filterData: any) => !!filterData?.filterApplied;

const mapLoanProductIdsToFormValues = (loanProductIds: any[]) => {
  const enumLoanTypes = getEnumList("LOAN_TYPE") ?? [];
  return loanProductIds
    .map((id: string) => enumLoanTypes.find((item: any) => item?.key === id)?.value)
    .filter((value: string | undefined): value is string => !!value);
};

const getAllLoanTypeFormValues = () => {
  return (getEnumList("LOAN_TYPE") ?? [])
    .map((item: any) => item?.value)
    .filter((value: string | undefined): value is string => !!value);
};

const getDefaultCaseIdentifierValue = () => {
  const central = (getEnumList("CASE_IDENTIFIER") ?? []).find(
    (item: any) =>
      item?.value?.toLowerCase() === "central" || item?.key?.toLowerCase() === "central"
  );
  return central?.key ? [central.key] : undefined;
};

const applyDefaultLoanTypeAndSource = (
  form: ReturnType<typeof Form.useForm>[0],
  dispatch: ReturnType<typeof useDispatch>
) => {
  const allLoanTypes = getAllLoanTypeFormValues();
  const defaultCaseIdentifier = getDefaultCaseIdentifierValue();
  form.setFieldsValue({
    LOAN_TYPE: allLoanTypes.length > 0 ? allLoanTypes : undefined,
    caseIdentifier: defaultCaseIdentifier,
  });
  dispatch(setLoanType(allLoanTypes.length > 0 ? allLoanTypes : undefined));
};

const mergeConnectorListItems = (
  apiItems: { key: string; title: string }[],
  savedItems: { key: string; title: string }[] = []
) => {
  const seen = new Set<string>();
  return [...savedItems, ...apiItems].filter((item) => {
    if (seen.has(item.key)) {
      return false;
    }
    seen.add(item.key);
    return true;
  });
};

const tickHierarchyRoot = (
  root: any,
  selectedKeys: any[],
  onSelectedKeysChange: (keys: any[]) => void
) => {
  if (!root?.key) {
    return;
  }
  const keys = [...selectedKeys];
  const childKeys = [root.key] as any[];
  if (root?.children?.length > 0) {
    const collect = (list: any[], acc: any[]) => {
      list?.forEach((item: any) => {
        acc.push(item.key);
        if (item?.children?.length > 0) {
          collect(item.children, acc);
        }
      });
    };
    collect(root.children, childKeys);
  }
  childKeys.forEach((keySplice) => {
    if (keys.findIndex((key) => key == keySplice) === -1) {
      keys.push(keySplice);
    }
  });
  onSelectedKeysChange(keys);
};

/** Lead filter UI aligned with dc-web-connector (hierarchy tree, source, loan type). */
const LeadFilterConnector: React.FunctionComponent = () => {
  const lead: any = useSelector((state: RootState) => state.lead);
  const appliedEmployeeIds = getAppliedEmployeeIds(lead?.filterData);
  const appliedConnectorIds = getAppliedConnectorIds(lead?.filterData);
  const appliedConnectorsFromFilter = (lead?.filterData?.appliedConnectors ??
    []) as { key: string; title: string }[];
  const [teamHierarchyFull, setTeamHierarchyFull] = useState([]) as any;
  const [teamSearch, setTeamSearch] = useState("");
  const [teamHierarchyId, setTeamHierarchyId] = useState(appliedEmployeeIds) as any;
  const teamHierarchyList = useMemo(
    () => filterHierarchyTree(teamHierarchyFull, teamSearch),
    [teamHierarchyFull, teamSearch]
  );
  const [connectorList, setConnectorList] = useState<{ key: string; title: string }[]>(
    appliedConnectorsFromFilter
  );
  const [connectorHierarchyId, setConnectorHierarchyId] = useState(appliedConnectorIds) as any;
  const [connectorSearch, setConnectorSearch] = useState("");
  const [connectorPageNo, setConnectorPageNo] = useState(1);
  const [connectorHasMore, setConnectorHasMore] = useState(false);
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { appColors }: any = useSelector((state: RootState) => state.common);

  useEffect(() => {
    if (isFilterApplied(lead?.filterData)) {
      const appliedLoanTypes = mapLoanProductIdsToFormValues(
        lead?.filterData?.loanProductIds ?? []
      );
      const loanTypeFormValue =
        appliedLoanTypes.length > 0
          ? appliedLoanTypes
          : Array.isArray(lead?.loanType)
          ? lead.loanType
          : lead?.loanType != null && lead?.loanType !== ""
          ? [lead.loanType]
          : undefined;

      const caseId = lead?.filterData?.caseIdentifier;
      form.setFieldsValue({
        LOAN_TYPE: loanTypeFormValue,
        caseIdentifier: caseId != null && caseId !== "" ? [caseId] : undefined,
      });
      if (loanTypeFormValue?.length) {
        dispatch(setLoanType(loanTypeFormValue));
      }
    } else {
      applyDefaultLoanTypeAndSource(form, dispatch);
    }

    setTeamHierarchyId(getAppliedEmployeeIds(lead?.filterData));
    setConnectorHierarchyId(getAppliedConnectorIds(lead?.filterData));
    if (appliedConnectorsFromFilter.length > 0) {
      setConnectorList(appliedConnectorsFromFilter);
    }
    getListEmployeeHierarchy();
  }, []);

  useEffect(() => {
    if ((teamHierarchyId?.length ?? 0) > 0) {
      getListConnectorHierarchy(connectorSearch, 1, false, teamHierarchyId);
    } else {
      setConnectorList([]);
      setConnectorHasMore(false);
    }
  }, [teamHierarchyId]);

  const applyTeamHierarchyResponse = (payload: any) => {
    const list = parseEmployeeHierarchyResponse(payload);
    setTeamHierarchyFull(list);
    if (!isFilterApplied(lead?.filterData) && list[0]?.key) {
      tickHierarchyRoot(list[0], [], setTeamHierarchyId);
    }
  };

  const getListEmployeeHierarchy = () => {
    API_SERVICE.getListEmployeeHierarchy({}).then(({ data }: any) => {
      if (data?.payload) {
        applyTeamHierarchyResponse(data.payload);
      }
    });
  };

  const onTeamSearch = (searchParam: string) => {
    setTeamSearch(searchParam);
  };

  const getListConnectorHierarchy = (
    searchParam: string = connectorSearch,
    pageNo: number = 1,
    append = false,
    userIds: string[] = teamHierarchyId
  ) => {
    if ((userIds?.length ?? 0) === 0) {
      setConnectorList([]);
      setConnectorHasMore(false);
      return;
    }
    API_SERVICE.getListHierarchy({
      userIds,
      searchParam: searchParam?.trim() || undefined,
      pageNo,
      pageSize: 10,
    }).then(({ data }: any) => {
      if (data?.payload) {
        const { list, last, pageNo: currentPage } = parseConnectorListResponse(data.payload);
        const mapped = list.map((item: any) => ({
          key: item.key,
          title: item.title,
        }));
        const savedConnectors = (lead?.filterData?.appliedConnectors ??
          []) as { key: string; title: string }[];
        setConnectorList((prev) => {
          const merged = append ? [...prev, ...mapped] : mapped;
          return mergeConnectorListItems(merged, savedConnectors);
        });
        setConnectorHasMore(!last);
        setConnectorPageNo(currentPage);
      }
    });
  };

  const onConnectorSearch = (searchParam: string) => {
    setConnectorSearch(searchParam);
    setConnectorPageNo(1);
    getListConnectorHierarchy(searchParam, 1, false);
  };

  const onConnectorLoadMore = () => {
    getListConnectorHierarchy(connectorSearch, connectorPageNo + 1, true);
  };

  const clearFilter = () => {
    form.resetFields();
    setTeamHierarchyId([]);
    setConnectorHierarchyId([]);
    setConnectorList([]);
    setConnectorSearch("");
    setConnectorPageNo(1);
    setConnectorHasMore(false);
    dispatch(setRestFilterData());
    applyDefaultLoanTypeAndSource(form, dispatch);
  };

  const onFinish = (e: any) => {
    const enumLoanTypes = getEnumList("LOAN_TYPE") ?? [];
    const selectedLoanTypeValues = Array.isArray(e?.LOAN_TYPE)
      ? e.LOAN_TYPE
      : e?.LOAN_TYPE != null && e?.LOAN_TYPE !== ""
      ? [e.LOAN_TYPE]
      : [];
    const loanProductIds = selectedLoanTypeValues
      .map((v: any) => enumLoanTypes.find((data: any) => data?.value === v)?.key)
      .filter((id: any) => id != null && id !== "");

    let caseIdentifier;
    if (Array.isArray(e?.caseIdentifier)) {
      caseIdentifier = e?.caseIdentifier?.at(0);
    }

    if (
      loanProductIds.length === 0 &&
      !caseIdentifier &&
      (teamHierarchyId?.length ?? 0) === 0 &&
      (connectorHierarchyId?.length ?? 0) === 0 &&
      !e?.assignToMe
    ) {
      notification.error({ message: "Please select one value of filter" });
    } else {
      const connectorIds = e?.assignToMe
        ? [getUserDetails().userId]
        : connectorHierarchyId;

      const param: Record<string, any> = {
        filterApplied: true,
        caseIdentifier: caseIdentifier,
        employeeIds: teamHierarchyId,
        connectorIds,
      };
      if (loanProductIds.length > 0) {
        param.loanProductIds = loanProductIds;
      }
      if (connectorIds.length > 0) {
        param.appliedConnectors = connectorIds.map((key: string) => {
          const found = connectorList.find((item) => item.key === key);
          return found ?? { key, title: key };
        });
      } else {
        param.appliedConnectors = [];
      }
      dispatch(setLoanType(selectedLoanTypeValues.length ? selectedLoanTypeValues : undefined));
      dispatch(setFilterData(param));
      dispatch(setLeadPageNo(1));
      dispatch(setIsShowFilter(false));
    }
  };

  const checkboxStyles: any = {
    "--bgColor": "#20A74E",
    paddingRight: 5,
  };


  return (
    <div>
      <Card className="lead-filter-panel" style={{ width: "100%", marginBottom: "30px" }}>
        <Title level={5} style={{ marginLeft: "5px" }}>
          <Row>
            <Col span={12}> Filter By:</Col>
            <Col span={12} style={{ display: "flex", justifyContent: "end" }}>
              <CloseOutlined
                onClick={() => {
                  dispatch(setIsShowFilter(false));
                }}
              />
            </Col>
          </Row>
        </Title>
        <Divider />
        <Form
          form={form}
          size="large"
          layout="vertical"
          autoComplete="off"
          onFinish={onFinish}
        >
          <Row gutter={24}>
            <Col xs={24} md={24} sm={3} lg={3}>
              <Form.Item name="caseIdentifier" label="Source">
                <Checkbox.Group>
                  <Space direction="vertical">
                    {getEnumList("CASE_IDENTIFIER")?.map((loan: any, i: number) => (
                      <Checkbox
                        key={i}
                        value={loan.key}
                        className="dynamic-pagination-checkbox"
                        style={checkboxStyles}
                      >
                        {loan.value}
                      </Checkbox>
                    ))}
                  </Space>
                </Checkbox.Group>
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={7}>
              <Form.Item
                name="LOAN_TYPE"
                valuePropName="value"
                label="Loan Type"
              >
                <LoanTypePicker
                  checkboxStyles={checkboxStyles}
                  loanTypeEnum={(getEnumList("LOAN_TYPE") ?? []) as { key: string; value: string }[]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={7}>
              <HierarchyTreeFilter
                label="Team"
                hierarchyList={teamHierarchyList}
                selectedKeys={teamHierarchyId}
                onSelectedKeysChange={setTeamHierarchyId}
                onSearch={onTeamSearch}
                checkboxStyles={checkboxStyles}
                immediateSearch
              />
            </Col>
            <Col xs={24} md={12} lg={7}>
              <FlatCheckboxListFilter
                label="Connector"
                items={connectorList}
                selectedKeys={connectorHierarchyId}
                onSelectedKeysChange={setConnectorHierarchyId}
                onSearch={onConnectorSearch}
                checkboxStyles={checkboxStyles}
                hasMore={connectorHasMore}
                onLoadMore={onConnectorLoadMore}
                emptyText={
                  (teamHierarchyId?.length ?? 0) === 0
                    ? "Select team member(s) first"
                    : "No connectors found"
                }
              />
            </Col>
          </Row>
          <Divider />
          <div style={{ textAlign: "right" }}>
            <Form.Item>
              <Space>
                <Button
                  size="large"
                  htmlType="button"
                  onClick={clearFilter}
                  className="dynamic-btn-default"
                  style={{ "--borderColor": appColors?.appSecondaryColor ?? "" } as any}
                >
                  Clear
                </Button>
                <Button
                  size="large"
                  type="primary"
                  htmlType="submit"
                  className="dynamic-btn-primary"
                  style={{ "--btnColor": appColors?.appPrimaryColor ?? "" } as any}
                >
                  Apply Filter
                </Button>
              </Space>
            </Form.Item>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default LeadFilterConnector;
